<script setup lang="ts">
import type { City } from "@classes-service-types/index.ts";
import type { ProfileAdminPermissionsRecords } from "@profile-service-types/index.ts";
import { PermissionRecordType } from "@profile-service-types/index.ts";
import { useVModel } from "@vueuse/core";
import { debounce } from "lodash";
import Checkbox from "primevue/checkbox";
import Chip from "primevue/chip";
import MultiSelect from "primevue/multiselect";
import Skeleton from "primevue/skeleton";
import { computed, defineEmits, reactive, ref, toRefs, watch } from "vue";
import { accountApplicationPresets } from "@/components/Account/styles";
import type { UndefinedToNull } from "@/components/Account/types.ts";
import { SESSION_TYPE } from "@/constants";
import { useGetCitiesData } from "@/providers/cities/useGetCitiesData";
import { useSearchCities } from "@/providers/cities/useSearchCities.ts";
import { useGetCountriesData } from "@/providers/countries/useGetCountriesData";
import type { LocalState, Props } from "./types.ts";
import { isStateEqual, localArrayBackToObject, updateOrRefreshOriginalProfileValues } from "./utils";

const props = defineProps<Props>();
const { editable, parentForm, originalProfile, cancelled } = toRefs(props);

const emit = defineEmits(["update:modelValue"]);
const profile = useVModel(props, "modelValue", emit);

const sessionTypesOptions = Object.values(SESSION_TYPE);

const localState = reactive<LocalState>({
  selectedCities: [],
  selectedCountries: [],
  allLocations: false,
  sessionTypes: [],
  allSessionTypes: false,
  tenantManger: false,
});

const profileAdminPermissionsRef = ref<UndefinedToNull<ProfileAdminPermissionsRecords>[]>([]);
const originalCitiesRef = ref<number[]>([]);
const originalCountriesRef = ref<{ [id: string]: ProfileAdminPermissionsRecords }>({});

const countriesQueryRef = ref("");
const { data: countriesData, isLoading: countriesIsLoading } = useGetCountriesData({
  limit: 1000,
  page: 1,
  q: countriesQueryRef,
});

watch([countriesData, originalCountriesRef], ([newCountriesData, newCountriesRef]) => {
  if (newCountriesData && newCountriesData?.length > 0 && newCountriesRef && Object.keys(newCountriesRef).length > 0) {
    localState.selectedCountries = countriesData.value?.filter((val) => !!newCountriesRef[val.id]) ?? [];
  }
});

const originalCitiesFetchEnabled = computed(() => originalCitiesRef.value.length > 0);
const { data: originalCites } = useGetCitiesData({
  limit: computed(() => originalCitiesRef.value.length),
  page: 1,
  city_ids: originalCitiesRef,
  in_use: false,
  useQueryOptions: { enabled: originalCitiesFetchEnabled },
});

watch([originalCites, originalCitiesRef], ([originalCitesData, originalCitiesRef]) => {
  if (originalCitesData) {
    localState.selectedCities = originalCitesData.map((val) => ({ ...val, showAll: cityStringRef.value }));
  }
});

const cityStringRef = ref("chi");
const {
  data: citiesData,
  isLoading: citiesIsLoading,
  isPending: citiesIsPending,
} = useSearchCities({ limit: 100, skip: 0, search: cityStringRef });

const cityOptionsAndCurrent = computed(() => {
  const retval = [...(citiesData?.value ?? [])] as Array<City & { optionDisabled?: boolean }>;
  if (originalCites.value?.length) {
    retval.unshift(...originalCites.value);
  }

  if (localState.selectedCities?.length) {
    for (const selectedCity of localState.selectedCities) {
      const foundCity = retval.find((value) => selectedCity.id === value.id);
      if (!foundCity) {
        retval.unshift(selectedCity);
      }
    }
  }

  if (citiesIsLoading.value || citiesIsPending.value) {
    retval.unshift({ city_ascii: "Loading ...", id: -1, optionDisabled: true });
  }

  return retval.map((val) => ({ ...val, showAll: cityStringRef.value }));
});

const selectedCitiesWithShowAll = computed(() => {
  return localState.selectedCities?.map((val) => ({ ...val, showAll: cityStringRef.value }));
});

const updateCitiesFilter = debounce((event) => {
  cityStringRef.value = event.value;
}, 1000);

watch(
  () => originalProfile.value.profile_admin_permissions,
  (profileAdminPermissions) => {
    updateOrRefreshOriginalProfileValues(
      profileAdminPermissions,
      originalCitiesRef,
      originalCountriesRef,
      localState,
      sessionTypesOptions,
    );
  },
  { immediate: true },
);

watch(cancelled, (cancelled) => {
  if (cancelled && originalProfile.value.profile_admin_permissions) {
    updateOrRefreshOriginalProfileValues(
      originalProfile.value.profile_admin_permissions,
      originalCitiesRef,
      originalCountriesRef,
      localState,
      sessionTypesOptions,
    );
  }
});

watch(
  [
    () => localState.allLocations,
    () => localState.allSessionTypes,
    () => localState.sessionTypes,
    () => localState.selectedCountries,
    () => localState.selectedCities,
  ],
  (afterStateArray, beforeStateArray) => {
    const afterState = localArrayBackToObject(afterStateArray);
    const beforeState = localArrayBackToObject(beforeStateArray);
    if (!isStateEqual(afterState, beforeState)) {
      let profileAdminPermissions: UndefinedToNull<ProfileAdminPermissionsRecords>[] = [];

      if (afterState.allLocations) {
        localState.selectedCities = [];
        localState.selectedCountries = [];
        profileAdminPermissions = [
          {
            permit_all_records: true,
            permission_record_type: PermissionRecordType.Country,
            permission_record_id: null,
          },
        ];
      } else {
        const cityPermissions = localState.selectedCities.map((val) => ({
          permit_all_records: null,
          permission_record_type: PermissionRecordType.City,
          permission_record_id: `${val.id}`,
        }));
        const countryPermissions = localState.selectedCountries.map((val) => ({
          permit_all_records: null,
          permission_record_type: PermissionRecordType.Country,
          permission_record_id: `${val.id}`,
        }));
        profileAdminPermissions = [...cityPermissions, ...countryPermissions];
      }

      if (afterState.allSessionTypes) {
        localState.sessionTypes = [];
        profileAdminPermissions.push({
          permit_all_records: true,
          permission_record_type: PermissionRecordType.SessionType,
          permission_record_id: null,
        });
      } else {
        const sessionTypePermissions = localState.sessionTypes.map((val) => ({
          permit_all_records: null,
          permission_record_type: PermissionRecordType.SessionType,
          permission_record_id: `${val.id}`,
        }));
        profileAdminPermissions = [...profileAdminPermissions, ...sessionTypePermissions];
      }

      profileAdminPermissionsRef.value = profileAdminPermissions;
      profile.value.profile_admin_permissions = <ProfileAdminPermissionsRecords[]>profileAdminPermissions;
      parentForm.value.setFieldValue(
        "profile_admin_permissions",
        <ProfileAdminPermissionsRecords[]>profileAdminPermissions,
      );
    }
  },
);
</script>

<template>
  <div class="grid grid-cols-3 gap-y-2 mt-2">
    <parentForm.Subscribe>
      <template v-slot="{ canSubmit, isSubmitting }">
        <!--  Locations  -->
        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Location Based-Permissions</label>
        </div>
        <div v-if="editable" class="col-span-2 flex items-center">
          <Checkbox :pt="accountApplicationPresets.checkbox" v-model="localState.allLocations" :binary="true" inputId="allLocal" name="allLocal" value="All Locations" />
          <label for="allLocal" class="ml-3">All Locations</label>
        </div>
        <div v-else-if="!editable && !isSubmitting" class="col-start-2 col-span-2">
          <span class="flex items-center" v-if="localState.allLocations">All</span>
        </div>
        <Skeleton v-else />

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Location Based-Permissions: Countries</label>
        </div>
        <div v-if="editable" class="col-span-2 flex items-center">
          <MultiSelect
            v-model="localState.selectedCountries"
            display="chip"
            :options="countriesData"
            optionLabel="name"
            placeholder="Select Countries"
            class="w-full md:w-20rem"
            aria-label="Countries Options"
            :showToggleAll="false"
            :filter="true"
            :loading="countriesIsLoading"
            :disabled="localState.allLocations"
          />
        </div>
        <div v-else-if="!editable && !isSubmitting && !countriesIsLoading" class="flex items-center col-span-2 flex-wrap gap-2">
          <Chip v-for="(country) in localState.selectedCountries" :key="country.id" :label="country.name" :pt="accountApplicationPresets.chip" class="mr-2"/>
        </div>
        <Skeleton v-else class="flex items-center col-span-2 flex-wrap gap-y-2" />


        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Location Based-Permissions: Cities</label>
        </div>
        <div v-if="editable" class="col-span-2 flex items-center">
          <!-- virtualScrollerOptions is only needed to keep options above the selector  -->
          <MultiSelect
            :model-value="selectedCitiesWithShowAll"
            @change="event => localState.selectedCities = event.value"
            display="chip"
            :options="cityOptionsAndCurrent"
            optionDisabled="optionDisabled"
            optionLabel="city_ascii"
            placeholder="Select Cities"
            class="w-full md:w-20rem"
            aria-label="Cities Options"
            :showToggleAll="false"
            :filter="true"
            :loading="citiesIsLoading"
            :filter-fields="['showAll']"
            @filter="updateCitiesFilter"
            :virtualScrollerOptions="{ itemSize: 44 }"
            :disabled="localState.allLocations"
          >
            <template v-slot:option="{option}">
              <div>
                <div class="font-semibold">{{ option.city_ascii }}</div>
                <div>
                  {{ option.country?.name }}
                  <span v-if="option.country?.name && option.admin_name_ascii">-</span>
                  {{ option.admin_name_ascii }}
                </div>
              </div>
            </template>
            <template v-slot:emptyfilter>
              <div v-if="citiesIsLoading || citiesIsPending">Loading ...</div>
              <div v-else>No Results Found</div>
            </template>
          </MultiSelect>
        </div>
        <div v-else-if="!editable && !isSubmitting && !citiesIsLoading" class="flex items-center col-span-2 flex-wrap gap-y-2">
          <Chip v-for="(city) in localState.selectedCities" :key="city.id" :label="city.city_ascii" :pt="accountApplicationPresets.chip" class="mr-2"/>
        </div>
        <Skeleton v-else class="flex items-center col-span-2 flex-wrap gap-y-2" />


        <!--  Session Types  -->
        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Session Type Permissions</label>
        </div>
        <div v-if="!editable" class="col-span-2 flex items-center">
          <span v-if="localState.allSessionTypes">All</span>
          <div class="flex items-center flex-wrap" v-else>
            <Chip v-for="(sessionType) in localState.sessionTypes" :key="sessionType.id" :label="sessionType.name" :pt="accountApplicationPresets.chip" class="h-1 mr-2 mb-2" />
          </div>
        </div>
        <template v-else-if="editable && !isSubmitting">
          <div class="col-span-2 flex items-center">
            <Checkbox :pt="accountApplicationPresets.checkbox" v-model="localState.allSessionTypes" :binary="true" inputId="allLocal" name="allLocal" value="All Locations" />
            <label for="allLocal" class="ml-3">All Session Types</label>
          </div>
          <div class="col-start-2 col-span-2 flex items-center">
            <MultiSelect
              v-model="localState.sessionTypes"
              display="chip"
              :options="sessionTypesOptions"
              optionLabel="name"
              placeholder="Select Types"
              class="w-full md:w-20rem"
              aria-label="Session Types"
              :showToggleAll="false"
              :filter="true"
              :disabled="localState.allSessionTypes"
            />
          </div>
        </template>
        <Skeleton v-else class="col-start-2 col-span-2" />


        <!--  Tenant Manager Yes/No  -->
<!--        <div class="h-12 flex items-center">-->
<!--          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Tenant Administrator</label>-->
<!--        </div>-->
<!--        <div v-if="editable" class="col-span-2 flex items-center">-->
<!--          <Checkbox :pt="accountApplicationPresets.checkbox" v-model="localState.tenantManger" :binary="true" inputId="tenantManager" name="tenantManager" value="Tenant Manager" />-->
<!--          <label for="tenantManager" class="ml-3">Yes</label>-->
<!--        </div>-->
<!--        <div v-else class="col-span-2 flex items-center">-->
<!--          <span class="">{{ localState.tenantManger ? "Yes": "No" }}</span>-->
<!--        </div>-->
      </template>
    </parentForm.Subscribe>
  </div>
</template>

<style scoped>

</style>
